/**
 * Pure H OS · Brand Assets · Single Source of Truth
 * ==================================================
 * Executive-luxury uploader for every brand asset consumed by
 * PDFs / reports / certificates / invoices / website.
 * Never upload logos inside individual modules — this is THE source.
 *
 * Namespaces:
 *   pureh              → holding-level assets (Pure H marks)
 *   <ventureId>        → venture-level assets (ORVAN marks, CEO signature)
 */
import { useCallback, useEffect, useState } from "react";
import { Upload, Trash2, Palette, Check, Image as ImageIcon } from "lucide-react";
import { Card, SectionHeader, Button, EmptyState } from "../../components/lib";

const API = process.env.REACT_APP_BACKEND_URL as string;

type AssetType =
  | "primary_logo" | "secondary_logo" | "white_logo" | "black_logo"
  | "favicon" | "official_stamp" | "ceo_signature";

const HOLDING_ASSETS: { key: AssetType; label: string }[] = [
  { key: "primary_logo",   label: "Primary Logo" },
  { key: "secondary_logo", label: "Secondary Logo" },
  { key: "white_logo",     label: "White Logo" },
  { key: "black_logo",     label: "Black Logo" },
  { key: "favicon",        label: "Favicon" },
  { key: "official_stamp", label: "Official Stamp" },
];

const VENTURE_ASSETS: { key: AssetType; label: string }[] = [
  ...HOLDING_ASSETS,
];

const EXEC_ASSETS: { key: AssetType; label: string }[] = [
  { key: "ceo_signature", label: "CEO Signature (transparent PNG/SVG)" },
];

// ── HTTP helper (Bearer + cookies) ──────────────────────────────
async function apiFetch(path: string, opts: RequestInit = {}) {
  const token = localStorage.getItem("pureh_auth_token");
  const headers = new Headers(opts.headers as any);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(`${API}${path}`, { ...opts, headers, credentials: "include" });
}

async function getBundle(namespace: string) {
  const r = await apiFetch(`/api/pureh/corporate-identity/brand-assets/${namespace}`);
  if (!r.ok) throw new Error(`Failed to load brand assets (${r.status})`);
  return r.json();
}

async function upload(namespace: string, assetType: string, file: File) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await apiFetch(
    `/api/pureh/corporate-identity/brand-assets/${namespace}/${assetType}`,
    { method: "PUT", body: fd },
  );
  if (!r.ok) throw new Error(`Upload failed (${r.status})`);
  return r.json();
}

async function removeAsset(namespace: string, assetType: string) {
  const r = await apiFetch(
    `/api/pureh/corporate-identity/brand-assets/${namespace}/${assetType}`,
    { method: "DELETE" },
  );
  if (!r.ok) throw new Error(`Delete failed (${r.status})`);
  return r.json();
}

async function saveIdentity(
  namespace: string, colors: Record<string, string>, fonts: Record<string, string>,
) {
  const r = await apiFetch(
    `/api/pureh/corporate-identity/brand-assets/${namespace}/identity`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ colors, fonts }),
    },
  );
  if (!r.ok) throw new Error(`Save failed (${r.status})`);
  return r.json();
}

// ── One asset card ──────────────────────────────────────────────
function AssetCard({
  namespace, assetKey, label, meta, onChanged,
}: {
  namespace: string;
  assetKey: AssetType;
  label: string;
  meta?: { uploadedAt?: string; sizeBytes?: number; mimeType?: string };
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!meta) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      const r = await apiFetch(
        `/api/pureh/corporate-identity/brand-assets/${namespace}/${assetKey}/binary`,
      );
      if (!r.ok) return;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (!cancelled) setPreview(url);
    })();
    return () => { cancelled = true; };
  }, [namespace, assetKey, meta?.uploadedAt]);

  const handleFile = useCallback(async (file: File) => {
    setBusy(true);
    try {
      await upload(namespace, assetKey, file);
      onChanged();
    } finally { setBusy(false); }
  }, [namespace, assetKey, onChanged]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(`Delete ${label}?`)) return;
    setBusy(true);
    try { await removeAsset(namespace, assetKey); onChanged(); }
    finally { setBusy(false); }
  }, [namespace, assetKey, label, onChanged]);

  return (
    <div
      data-testid={`brand-asset-card-${namespace}-${assetKey}`}
      className="rounded-md p-4 border"
      style={{ borderColor: "var(--ph-border-strong)", background: "var(--ph-surface)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
            {label}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {meta ? `${(meta.sizeBytes! / 1024).toFixed(1)} KB · ${meta.mimeType}` : "Not uploaded"}
          </div>
        </div>
        {meta && <Check size={14} className="text-emerald-500 mt-1" />}
      </div>

      <div
        className="mt-3 rounded-md flex items-center justify-center overflow-hidden"
        style={{ height: 96, background: "var(--ph-neutral-tint)", border: "1px dashed var(--ph-border)" }}
      >
        {preview ? (
          <img
            src={preview}
            alt={label}
            className="max-h-full max-w-full object-contain"
            data-testid={`brand-asset-preview-${namespace}-${assetKey}`}
          />
        ) : (
          <ImageIcon size={22} className="text-slate-300" />
        )}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <label
          className="text-xs px-3 py-1.5 rounded cursor-pointer border"
          style={{ borderColor: "var(--ph-border-strong)" }}
          data-testid={`brand-asset-upload-${namespace}-${assetKey}`}
        >
          <Upload size={12} className="inline mr-1" />
          {meta ? "Replace" : "Upload"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            disabled={busy}
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </label>
        {meta && (
          <Button
            variant="ghost"
            onClick={handleDelete}
            testId={`brand-asset-delete-${namespace}-${assetKey}`}
          >
            <Trash2 size={12} /> Delete
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Group section ───────────────────────────────────────────────
function AssetGroup({
  title, namespace, items, assets, onChanged,
}: {
  title: string;
  namespace: string;
  items: { key: AssetType; label: string }[];
  assets: Record<string, any>;
  onChanged: () => void;
}) {
  return (
    <div className="mb-6" data-testid={`brand-asset-group-${namespace}`}>
      <div
        className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3"
        style={{ color: "var(--ph-ink-muted)" }}
      >
        {title}
      </div>
      <div className="grid grid-cols-3 gap-4">
        {items.map((a) => (
          <AssetCard
            key={a.key}
            namespace={namespace}
            assetKey={a.key}
            label={a.label}
            meta={assets[a.key]}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main tab component ──────────────────────────────────────────
export default function BrandAssetsTab({ ventureId }: { ventureId: string }) {
  const [holding, setHolding] = useState<any>({ assets: {}, identity: {} });
  const [venture, setVenture] = useState<any>({ assets: {}, identity: {} });
  const [loading, setLoading] = useState(true);
  const [colors, setColors] = useState<Record<string, string>>({});
  const [fonts, setFonts]   = useState<Record<string, string>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [h, v] = await Promise.all([getBundle("pureh"), getBundle(ventureId)]);
      setHolding(h);
      setVenture(v);
      setColors(v?.identity?.colors || {});
      setFonts(v?.identity?.fonts || {});
    } finally { setLoading(false); }
  }, [ventureId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <EmptyState title="Loading Brand Assets…" />;

  return (
    <>
      <Card>
        <SectionHeader
          eyebrow="Brand Assets · Single source of truth"
          title="Brand Assets"
          action={
            <div className="text-[11px] text-slate-500">
              Every PDF, report and future website reads brand imagery from here only.
            </div>
          }
        />

        <AssetGroup
          title="Pure H (Holding)"
          namespace="pureh"
          items={HOLDING_ASSETS}
          assets={holding.assets || {}}
          onChanged={reload}
        />

        <AssetGroup
          title={`${ventureId.toUpperCase()} (Venture)`}
          namespace={ventureId}
          items={VENTURE_ASSETS}
          assets={venture.assets || {}}
          onChanged={reload}
        />

        <AssetGroup
          title="Executive"
          namespace={ventureId}
          items={EXEC_ASSETS}
          assets={venture.assets || {}}
          onChanged={reload}
        />
      </Card>

      <Card className="mt-6">
        <SectionHeader
          eyebrow="Brand Identity"
          title="Colors & Fonts"
          action={
            <Button
              variant="primary"
              testId="save-brand-identity"
              onClick={async () => {
                await saveIdentity(ventureId, colors, fonts);
                await reload();
              }}
            >
              <Palette size={14} /> Save Identity
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-6">
          {/* Colors */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3"
                  style={{ color: "var(--ph-ink-muted)" }}>Brand Colors</div>
            {(["primary", "secondary", "accent", "ink", "ivory", "gold"] as const).map((k) => (
              <div key={k} className="flex items-center gap-3 mb-2">
                <div className="w-28 text-xs uppercase tracking-widest text-slate-500">{k}</div>
                <input
                  type="color"
                  data-testid={`brand-color-${k}`}
                  value={colors[k] || "#111111"}
                  onChange={(e) => setColors({ ...colors, [k]: e.target.value })}
                  className="h-8 w-16 rounded cursor-pointer border"
                />
                <input
                  type="text"
                  value={colors[k] || ""}
                  onChange={(e) => setColors({ ...colors, [k]: e.target.value })}
                  className="px-2 py-1 text-xs border rounded flex-1"
                  placeholder="#171719"
                  style={{ borderColor: "var(--ph-border-strong)" }}
                />
              </div>
            ))}
          </div>

          {/* Fonts */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3"
                  style={{ color: "var(--ph-ink-muted)" }}>Brand Fonts</div>
            {(["display", "body", "mono", "script"] as const).map((k) => (
              <div key={k} className="flex items-center gap-3 mb-2">
                <div className="w-28 text-xs uppercase tracking-widest text-slate-500">{k}</div>
                <input
                  type="text"
                  data-testid={`brand-font-${k}`}
                  value={fonts[k] || ""}
                  onChange={(e) => setFonts({ ...fonts, [k]: e.target.value })}
                  className="px-2 py-1 text-xs border rounded flex-1"
                  placeholder={k === "display" ? "Times New Roman" : k === "script" ? "Great Vibes" : "Helvetica"}
                  style={{ borderColor: "var(--ph-border-strong)" }}
                />
              </div>
            ))}
          </div>
        </div>
      </Card>
    </>
  );
}
